<template>
  <div class="p-the-j05-chat-screen-panel">
    <div
      v-if="shownDate || shownUnreadLabel"
      class="p-the-j05-chat-screen-panel__notifications"
    >
      <div
        v-if="shownDate"
        class="p-the-j05-chat-screen-panel__notifications__item p-the-j05-chat-screen-panel__date"
      >
        <span class="p-the-j05-chat-screen-panel__date__text">{{ date }}</span>
      </div>
    </div>

    <J05ChatScreenPanelMessage
      class="p-the-j05-chat-screen-panel__message"
      :chat-data="chatData"
      :is-sp="isSp"
      :is-you-sent="isYouSent"
      :class-name-direction="classNameDirection"
      :partner-user-image="partnerUserImage"
    >
      <template #content>
        <TheJ05NotificationPanelThanks
          v-if="isThanksMessage"
          :title="title"
          :point="point"
          :message="message"
          :badge-icon="badgeIcon"
          :badge-name="badgeName"
          :is-you-sent="isYouSent"
        />

        <J05ChatScreenPanelMessageBody
          v-else-if="message"
          :message-data="{ message }"
          :is-you-sent="isYouSent"
          :class-name-direction="classNameDirection"
        />
      </template>
    </J05ChatScreenPanelMessage>
  </div>
</template>

<script lang="ts">
import J05ChatScreenPanelMessage from "@/general/components/pages/parts/ThanksPointMessage/J05ChatScreenPanelMessage.vue";
import J05ChatScreenPanelMessageBody from "@/general/components/pages/parts/ThanksPointMessage/J05ChatScreenPanelMessageBody.vue";
import { Options, Vue } from "vue-class-component";
import globals from "@/general/store/modules/globals";
import commons from "@/common/store/modules/commons";
import * as dayjs from "dayjs";
import "dayjs/locale/zh-cn"; // import locale
import utc from "dayjs/plugin/utc";
import { ChatScreenPanelData } from "@/general/store/models";
import TheJ05NotificationPanelThanks
  from "@/general/components/pages/parts/ThanksPointMessage/TheJ05NotificationPanelThanks.vue";


// dayjs.extend(LocalizedFormat);
dayjs.extend(utc);

@Options({
  props: {
    chatData: { type: Object, required: true },
    unreadKeyNo: { type: Number, required: true },
    partnerUserImage: { type: String, default: "" }
  },
  components: {
    TheJ05NotificationPanelThanks,
    J05ChatScreenPanelMessage,
    J05ChatScreenPanelMessageBody
  },
  watch: {
      chatData: {
        handler(this: J05ChatScreenPanel) {
          this.formattedDate();
          this.initShownDate();
        },
        immediate: true,
        deep: true
      }
    }
})
export default class J05ChatScreenPanel extends Vue {
  chatData!: ChatScreenPanelData;
  unreadKeyNo!: number;

  isSp = globals.isSpScreenWidth;
  myId = commons.userInfo.id;
  date = "";
  shownDate = true;
  classNameDirection = {
    true: "--r",
    false: "--l"
  };
  partnerUserImage!: string;

  get chatLog() {
    return this.chatData.chatLog;
  }

  get unread() {
    return this.chatData.unread;
  }

  get key() {
    return this.chatData?.key ?? -1;
  }

  get isYouSent() {
    return this.chatData.isYouSent;
  }

  get message() {
    return this.chatData.message;
  }

  get title() {
    return this.chatData.title;
  }

  get isThanksMessage() {
    return this.chatData.isThanksMessage;
  }

  get point() {
    return this.chatData.point;
  }

  get badgeIcon() {
    return this.chatData.badgeIcon;
  }

  get badgeName() {
    return this.chatData.badgeName;
  }

  created() {
    this.formattedDate();
    this.initShownDate();
  }

  formattedDate() {
    const date = this.chatData.date;
    // console.log("inner " + date);

    this.date = dayjs.utc(date).format("YYYY/M/D(ddd)");
    // console.log("this" + this.date);
  }

  initShownDate() {
    if (!this.chatData) {
      return false;
    }

    const key = this.chatData.key;
    const chatLog = this.chatData.chatLog;

    if (!chatLog || !Array.isArray(chatLog)) {
      this.shownDate = key === 0;
      return false;
    }

    if (key === 0) {
      this.shownDate = true;
      return true;
    }

    if (key < 0 || key >= chatLog.length) {
      this.shownDate = false;
      return false;
    }
    const prevDate = chatLog[key - 1]?.date;
    const currentDate = this.chatData.date;

    if (!prevDate || !currentDate) {
      this.shownDate = true;
      return false;
    }

    const prevLocalDate = dayjs.utc(prevDate);
    const currentLocalDate = dayjs.utc(currentDate);

    const formattedPrevDate = prevLocalDate.format("YYYY-MM-DD");
    const formattedCurrentDate = currentLocalDate.format("YYYY-MM-DD");

    this.shownDate = formattedPrevDate !== formattedCurrentDate;

    return true;
  }

}
</script>

<style lang="scss" scoped>
.p-the-j05-chat-screen-panel {
  &__notifications {
    margin: rem(12) 0;

    &__item {
      @include pc {
        margin-top: rem(16);
      }
      @include sp {
        margin-top: rem(8);
      }
    }
  }

  &__date {
    text-align: center;

    &__text {
      @include pc {
        min-width: rem(96);
        font-size: rem(14);
      }
      @include sp {
        min-width: rem(64);
        font-size: rem(11);
      }
      background-color: #73767a;
      display: inline-block;
      margin: 0 auto;
      text-align: center;
      position: relative;
      line-height: 1.8;
      padding: 0 rem(8);
      border-radius: rem(8);
      color: #fff;
    }
  }

  &__unread {
    text-align: center;
    background-color: $common-white;
    overflow: hidden;

    &__text {
      @include pc {
        font-size: rem(14);
      }
      @include sp {
        font-size: rem(11);
      }
      line-height: 1.8;
    }
  }
}
</style>
